import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import {
  getNaicsFields,
  getStateDisplay,
} from "../../../utils/websiteVisitsHelpers";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import BusinessIcon from "@mui/icons-material/Business";
import EqualizerIcon from "@mui/icons-material/Equalizer";
import ContactsIcon from "@mui/icons-material/Contacts";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LanguageIcon from "@mui/icons-material/Language";
import CloseIcon from "@mui/icons-material/Close";
import { formatPhone } from "../../../utils/Phone";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";

const SOURCE_COLORS = ["#00336C", "#648315", "#4F8AD9", "#9BBF30", "#7A7A7A", "#9E9E9E"];

/* helpers */
function clean(v) {
  if (v == null) return "";
  const s = String(v).trim();
  const low = s.toLowerCase();
  if (!s || low.startsWith("translation missing:") || low.startsWith("en.") || low.includes("unknown_")) return "";
  return s;
}

function toHostname(url) {
  try {
    const u = new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`);
    return u.hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

function formatEmployees(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return clean(v) || "—";
  return n.toLocaleString();
}

/* ---- Sub-components ---- */

function ModeTab({ active, onClick, icon, label, disabled }) {
  return (
    <Button
      size="small"
      startIcon={icon}
      onClick={onClick}
      disabled={disabled}
      sx={{
        px: 1.5,
        py: 0.5,
        borderRadius: 1.5,
        fontWeight: 700,
        fontSize: 13,
        textTransform: "none",
        minHeight: 28,
        ...(active
          ? {
              backgroundColor: BRAND_BLUE,
              color: "#ffffff",
              border: `1.5px solid ${BRAND_BLUE}`,
              "&:hover": { backgroundColor: "#002857" },
            }
          : {
              color: BRAND_BLUE,
              backgroundColor: "transparent",
              border: "1.5px solid rgba(0,51,108,0.2)",
              "&:hover": { backgroundColor: "rgba(0,51,108,0.05)", borderColor: "rgba(0,51,108,0.4)" },
            }),
        "&.Mui-disabled": { opacity: 0.4 },
      }}
    >
      {label}
    </Button>
  );
}

function InfoField({ label, value }) {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: "0.75rem",
          fontWeight: 700,
          color: BRAND_GREEN,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          mb: 0.3,
        }}
      >
        {label}
      </Typography>
      {typeof value === "string" || value == null ? (
        <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: BRAND_BLUE, wordBreak: "break-word" }}>
          {value || "—"}
        </Typography>
      ) : (
        value
      )}
    </Box>
  );
}

function MetricCard({ label, value, onClick, clickable = false, helperText = "" }) {
  const isInteractive = clickable && typeof onClick === "function";
  return (
    <Box
      onClick={isInteractive ? onClick : undefined}
      onKeyDown={
        isInteractive
          ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }
          : undefined
      }
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      sx={{
        flex: 1,
        p: 1.5,
        borderRadius: 2,
        border: isInteractive ? `1.5px solid rgba(0,51,108,0.25)` : "1.5px solid #dce4ef",
        backgroundColor: isInteractive ? "rgba(0,51,108,0.04)" : "#fafbfd",
        cursor: isInteractive ? "pointer" : "default",
        transition: "all 0.15s ease",
        "&:hover": isInteractive ? { backgroundColor: "rgba(0,51,108,0.08)", borderColor: "rgba(0,51,108,0.4)" } : {},
        "&:focus-visible": isInteractive ? { outline: `2px solid ${BRAND_BLUE}`, outlineOffset: 2 } : {},
      }}
    >
      <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: BRAND_GREEN, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5 }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: BRAND_BLUE }}>
          {value}
        </Typography>
        {isInteractive && <ArrowForwardIcon sx={{ color: BRAND_BLUE, fontSize: 18, opacity: 0.7 }} />}
      </Box>
      {helperText && (
        <Typography sx={{ mt: 0.25, fontSize: 12, fontWeight: 600, color: "rgba(0,51,108,0.5)", textTransform: "uppercase", letterSpacing: 0.2 }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
}

function TrafficSourcePie({ data }) {
  return (
    <Box sx={{ width: "100%", height: 240 }}>
      <ResponsiveContainer>
        <PieChart>
          <Pie data={data} dataKey="count" nameKey="source" outerRadius={80} label>
            {data.map((_, index) => (
              <Cell key={index} fill={SOURCE_COLORS[index % SOURCE_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
}

/* ---- Main Panel ---- */
export default function WebsiteVisitPanel({
  group,
  onOpenContactsModal,
  deriveCountry,
  onClose,
  trafficSources = [],
  trafficSourcesLoading = false,
}) {
  const [mode, setMode] = useState("details");
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);

  const record = group?.representative || {};

  if (!group) {
    return (
      <Box
        sx={{
          border: `1.5px solid ${BRAND_BLUE}`,
          borderRadius: 2,
          boxShadow: "0 8px 20px rgba(15,23,42,0.08)",
          background: "#ffffff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <LanguageIcon sx={{ fontSize: 48, color: "rgba(0,51,108,0.12)", mb: 1.5, display: "block", mx: "auto" }} />
          <Typography sx={{ color: "rgba(0,0,0,0.35)", fontSize: "0.9rem", fontWeight: 500 }}>
            Select a company to view details
          </Typography>
        </Box>
      </Box>
    );
  }

  const pages = (group.pages || []).filter((p) => p && p.url);
  const visitorAccountId = record?.visitor_account_id || record?._lfapp_account_value || null;

  const name = clean(record?.visitor_company_name) || clean(record?.visitor_account_name) || "Unknown Company";
  const city = clean(record?.visitor_city) || "—";
  const county = clean(record?.visitor_county);
  const country = deriveCountry ? deriveCountry(record) : clean(record?.visitor_country);
  const state = getStateDisplay(record?.visitor_state, record?.visitor_country || country) || "—";
  const phoneRaw = clean(record?.visitor_phone);
  const phone = phoneRaw ? formatPhone(phoneRaw) : "—";
  const employees = record?.visitor_employees ?? record?.numberofemployees ?? null;
  const rawWebsite = clean(record?.visitor_website) || clean(record?.visitor_domain);
  const websiteHost = rawWebsite ? toHostname(rawWebsite) : "";
  const websiteHref = websiteHost ? `https://${websiteHost}` : "";
  const { naicsName, naicsCode } = getNaicsFields(record);
  const sector = group?.sector || record?.sector;
  const description = clean(record?.visitor_description);
  const lastSeen = record?.last_seen ? new Date(record.last_seen).toLocaleString() : "—";

  const handleOpenContacts = () => { if (onOpenContactsModal) onOpenContactsModal(); };

  return (
    <Box
      sx={{
        border: `1.5px solid ${BRAND_BLUE}`,
        borderRadius: 2,
        boxShadow: "0 8px 20px rgba(15,23,42,0.08)",
        background: "#ffffff",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.5,
          borderBottom: "1px solid #dce4ef",
          flexShrink: 0,
          position: "relative",
        }}
      >
        <IconButton
          aria-label="Close website visitor details"
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: "#D32F2F",
            "&:hover": { backgroundColor: "rgba(211,47,47,0.08)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Typography
          sx={{
            fontSize: "1.15rem",
            fontWeight: 800,
            color: BRAND_BLUE,
            mb: 1.25,
            pr: 4,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {name}
        </Typography>
        <Box sx={{ display: "flex", gap: 0.75 }}>
          <ModeTab
            active={mode === "details"}
            onClick={() => setMode("details")}
            icon={<BusinessIcon sx={{ fontSize: 13 }} />}
            label="Details"
          />
          <ModeTab
            active={mode === "metrics"}
            onClick={() => setMode("metrics")}
            icon={<EqualizerIcon sx={{ fontSize: 13 }} />}
            label="Metrics"
          />
          <ModeTab
            active={false}
            onClick={handleOpenContacts}
            disabled={!visitorAccountId}
            icon={<ContactsIcon sx={{ fontSize: 13 }} />}
            label="Contacts"
          />
        </Box>
      </Box>

      {/* Scrollable content */}
      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", p: 2 }}>
        {mode === "details" ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {/* Main info grid */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 1.25,
                p: 1.5,
                borderRadius: 1.5,
                backgroundColor: "#fafbfd",
                border: "1px solid #edf0f5",
              }}
            >
              <InfoField label="Country" value={country || "—"} />
              <InfoField label="Sector" value={sector || "—"} />
              <InfoField label="State / Province" value={state} />
              <InfoField label="NAICS Text" value={naicsName || "—"} />
              <InfoField label="City" value={city} />
              <InfoField label="NAICS Code" value={naicsCode || "—"} />
              <InfoField label="County" value={county || "—"} />
              <InfoField label="Employees" value={formatEmployees(employees)} />
              <InfoField label="Phone Number" value={phone} />
              <Box />
              <Box sx={{ gridColumn: "1 / -1" }}>
                <InfoField label="Last Seen" value={lastSeen} />
              </Box>
            </Box>

            {/* Website */}
            {websiteHref && (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  backgroundColor: "#fafbfd",
                  border: "1px solid #edf0f5",
                }}
              >
                <InfoField
                  label="Website"
                  value={
                    <Chip
                      label={websiteHost}
                      component="a"
                      href={websiteHref}
                      target="_blank"
                      clickable
                      size="small"
                      sx={{
                        mt: 0.5,
                        backgroundColor: BRAND_BLUE,
                        color: "white",
                        fontWeight: 600,
                        "& .MuiChip-label": { px: 1.5 },
                      }}
                    />
                  }
                />
              </Box>
            )}

            {/* Description */}
            {description && (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  backgroundColor: "#fafbfd",
                  border: "1px solid #edf0f5",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    color: BRAND_GREEN,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    mb: 0.75,
                  }}
                >
                  Description
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.95rem",
                    color: BRAND_BLUE,
                    lineHeight: 1.65,
                    display: "-webkit-box",
                    WebkitLineClamp: descriptionExpanded ? "none" : 4,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {description}
                </Typography>
                {description.length > 180 && (
                  <Typography
                    onClick={() => setDescriptionExpanded((v) => !v)}
                    sx={{
                      mt: 0.75,
                      fontSize: 13,
                      fontWeight: 700,
                      color: BRAND_GREEN,
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                  >
                    {descriptionExpanded ? "Show less" : "Read more"}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        ) : (
          /* Metrics mode */
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            <Box sx={{ display: "flex", gap: 1.25 }}>
              <MetricCard
                label="Pages Viewed"
                value={pages.length}
                clickable
                helperText={pages.length ? "Click to view pages" : "No pages recorded"}
                onClick={() => { if (!pages.length) return; group?.onOpenPages?.(group); }}
              />
              <MetricCard
                label="Most Recent Visit"
                value={record?.last_seen ? dayjs(record.last_seen).format("MMM D, YYYY") : "—"}
              />
            </Box>

            <MetricCard
              label="Total Visits (All Time)"
              value={group?.total_visits ?? "—"}
            />

            {/* Traffic sources */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: "#fafbfd",
                border: "1px solid #edf0f5",
              }}
            >
              <Typography
                sx={{
                  mb: 1,
                  fontWeight: 700,
                  color: BRAND_GREEN,
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                Traffic Sources
              </Typography>

              {trafficSourcesLoading ? (
                <Typography sx={{ fontSize: "0.95rem", color: "rgba(0,0,0,0.4)" }}>
                  Loading traffic sources…
                </Typography>
              ) : trafficSources?.length ? (
                <TrafficSourcePie
                  data={trafficSources.map((r) => ({
                    source: r.source || r.name || "Unknown",
                    count: Number(r.count ?? r.value ?? 0),
                  }))}
                />
              ) : (
                <Typography sx={{ fontSize: "0.95rem", color: "rgba(0,0,0,0.4)" }}>
                  No traffic source data available
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
